/**
 * The identity challenge, solved in the browser (owner, 2026-10-17; ADR 0071): before the email-first step, a sign-in
 * and a self-serve sign-up, a fresh ALTCHA challenge (`GET /v1/auth/challenge`) solved with WebCrypto — the help
 * assistant's own solver (`@wetalk/help-widget/anonymous`), as the help fallback screen solves its own — and sent as
 * `altcha`. Each request needs its own: a solved challenge is accepted once.
 *
 * WHY IT LIVES IN THE GATEWAY LAYER. The api gateway's `discoverIdentifier`, `logIn` and `signUp` solve one THEMSELVES
 * whenever their caller passed no guard ({@link entryGuardOrSolved}), so no screen can forget it: the invitation pages'
 * own "Log in" (join screen, invitation acceptance) once sent none and was refused `403 identity_challenge_invalid`.
 * The entry card still passes its guard explicitly, because only it knows the trap field's value.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import { altchaPayloadOf, challengeOf, solveAltcha, type SubtleLike } from "@wetalk/help-widget/anonymous";

import { portalText } from "../i18n/portal.js";
import type { EntryGuardView } from "./gateway.js";

/** The challenge could not be read or solved: the request is not sent. */
export class IdentityChallengeUnsolvedError extends Error {
  override readonly name = "IdentityChallengeUnsolvedError";
}

/** A fresh identity challenge from `read` (the `{ data }` body), solved: the `altcha` value of one request. */
export async function solvedIdentityChallengeOf(read: () => Promise<unknown>, subtle: SubtleLike = globalThis.crypto.subtle): Promise<string> {
  const challenge = challengeOf(await read());
  if (challenge === null) throw new IdentityChallengeUnsolvedError(portalText("core", "auth.identity_challenge.unsolved"));
  const solution = await solveAltcha(subtle, challenge, () => false);
  if (solution === null) throw new IdentityChallengeUnsolvedError(portalText("core", "auth.identity_challenge.unsolved"));
  return altchaPayloadOf(challenge, solution);
}

/** The caller's guard, or — when it passed none — an empty trap and a challenge solved now. */
export async function entryGuardOrSolved(guard: EntryGuardView | undefined, client: WeTalkApiClient): Promise<EntryGuardView> {
  if (guard !== undefined) return guard;
  return { entryMarker: "", altcha: await solvedIdentityChallengeOf(async () => assertOk(await client.createIdentityChallenge())) };
}
